import { useState, useEffect } from 'react';
import { contractsAPI, menusAPI, selectableItemsAPI, clientPortalAPI, rentalsAPI, equipmentAPI, emailAPI, clientsAPI, eventTypesAPI, eventPackageTemplatesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Strip empty/blank tier rows and coerce types before sending to backend.
// Returns [] (not null) so the backend will actually overwrite/clear stored tiers.
const sanitizeTiers = (tiers) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return [];
  return tiers
    .filter(t => (t.label || '').toString().trim() || t.quantity || t.price_per_person)
    .map(t => ({
      label: (t.label || '').toString().trim() || null,
      quantity: t.quantity ? parseInt(t.quantity) : null,
      price_per_person: t.price_per_person ? parseFloat(t.price_per_person) : null,
    }));
};

const INITIAL_FORM_DATA = {
  clientName: '', clientEmail: '', clientPhone: '', clientId: null,
  secondaryClientName: '', secondaryClientEmail: '', secondaryClientPhone: '',
  billingAddress: '', eventName: '', eventType: 'offsite',
  eventDate: '', eventTime: '', guestCount: '', location: '', locationType: 'onsite',
  horsDoeuvresService: '', chafingDishCount: '',
  specialRequests: '', includeGratuity: false, gratuityRate: '0.18',
  kitchenNotes: '', assignedStaff: [],
  serviceStyle: '', barType: '', drinksPerPerson: 3, drinkPrice: null, estimatedDrinkTotal: null,
  eventStartTime: '', breakfastTime: '', brunchTime: '', lunchTime: '',
  cocktailHourTime: '', dinnerStartTime: '', lateNightTime: '', eventEndTime: '',
  tableStyle: '', tableclothColor: '', napkinColor: '',
  djTable: false, giftTable: false, headTable: false, waterStation: false,
  coffeeStation: false, barSetup: false, cakeTable: false, registrationTable: false, dessertStation: false,
  tournamentSetup: false, prizeTables: false, seeFloorplan: false, saladStation: false,
  buffetLightUpWall: false, buffetFrontOfRoom: false, dessertBayWindow: false, cocktailTables: false,
  podium: false, microphone: false,
  customAddons: [],
  discounts: [],
  menuPriceOverrides: {},
  customMenu: '',
  customMenuBreakfast: '',
  customMenuLunch: '',
  customMenuCocktail: '',
  customMenuDinner: '',
  customMenuDessert: '',
  coffeeService: false,
  coffeeNotes: '',
  customMenuLateNight: '',
  customMenuBreakfastPrice: '',
  customMenuLunchPrice: '',
  customMenuCocktailPrice: '',
  customMenuDinnerPrice: '',
  customMenuDessertPrice: '',
  customMenuLateNightPrice: '',
  customMenuBreakfastTiers: [],
  customMenuLunchTiers: [],
  customMenuCocktailTiers: [],
  customMenuDinnerTiers: [],
  customMenuDessertTiers: [],
  customMenuLateNightTiers: [],
  customMenuBar: '',
  customMenuBeverage: '',
  customMenuOnTheTurn: '',
  customMenuBarPrice: '',
  customMenuBeveragePrice: '',
  customMenuOnTheTurnPrice: '',
  customMenuBarTiers: [],
  customMenuBeverageTiers: [],
  customMenuOnTheTurnTiers: [],
  room: '',
  dietaryVegan: 0, dietaryVegetarian: 0, dietaryGlutenFree: 0,
  dietaryNutAllergy: 0, dietaryDairyFree: 0, dietaryHalal: 0, dietaryKosher: 0,
  dietaryOther: '',
  deliveryContactName: '',
  deliveryContactPhone: ''
};

export const useContractForm = () => {
  const { toast } = useToast();
  
  // Core data
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [menus, setMenus] = useState([]);
  const [clients, setClients] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState('date_asc'); // 'date_asc' | 'client_asc' | 'status'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'pending' | 'confirmed' | 'completed'
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  
  // Selections
  const [selectedMenuIds, setSelectedMenuIds] = useState([]);
  const [selectedRentals, setSelectedRentals] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [selectedHorsDoeuvresItems, setSelectedHorsDoeuvresItems] = useState([]);
  const [selectedLateNightItems, setSelectedLateNightItems] = useState([]);
  const [selectedBuffetItems, setSelectedBuffetItems] = useState([]);
  const [selectedBarItems, setSelectedBarItems] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedOnTheTurnItems, setSelectedOnTheTurnItems] = useState([]);
  const [selectedPlatedAppetizer, setSelectedPlatedAppetizer] = useState(null);
  const [selectedPlatedSalad, setSelectedPlatedSalad] = useState(null);
  const [selectedPlatedMains, setSelectedPlatedMains] = useState([]);
  const [selectedPlatedDessert, setSelectedPlatedDessert] = useState(null);
  const [platedCourses, setPlatedCourses] = useState(3);
  
  // Selectable items data
  const [horsDoeuvresItems, setHorsDoeuvresItems] = useState([]);
  const [lateNightItems, setLateNightItems] = useState([]);
  const [buffetItems, setBuffetItems] = useState([]);
  const [barItems, setBarItems] = useState([]);
  const [addonItems, setAddonItems] = useState([]);
  const [onTheTurnItems, setOnTheTurnItems] = useState([]);
  const [platedItems, setPlatedItems] = useState([]);
  
  // Section visibility
  const [showTimingSection, setShowTimingSection] = useState(false);
  const [showSetupSection, setShowSetupSection] = useState(false);
  const [showTablePlannerSection, setShowTablePlannerSection] = useState(false);
  const [showRentalsSection, setShowRentalsSection] = useState(false);
  const [showEquipmentSection, setShowEquipmentSection] = useState(false);
  const [showBarSection, setShowBarSection] = useState(false);
  const [showTravelFeeSection, setShowTravelFeeSection] = useState(false);
  const [showHorsDoeuvresSection, setShowHorsDoeuvresSection] = useState(false);
  const [showLateNightSection, setShowLateNightSection] = useState(false);
  const [showBuffetSection, setShowBuffetSection] = useState(false);
  const [showBarItemsSection, setShowBarItemsSection] = useState(false);
  const [showAddonsSection, setShowAddonsSection] = useState(false);
  const [showCustomAddonsSection, setShowCustomAddonsSection] = useState(false);
  const [showPlatedSection, setShowPlatedSection] = useState(false);
  const [showCustomMenuSection, setShowCustomMenuSection] = useState(false);
  const [showOnTheTurnSection, setShowOnTheTurnSection] = useState(false);
  const [packageTemplates, setPackageTemplates] = useState([]);
  
  // Travel fee
  const [travelFee, setTravelFee] = useState(0);
  const [travelDistanceKm, setTravelDistanceKm] = useState(null);
  const [travelChargeableKm, setTravelChargeableKm] = useState(null);
  
  // Payment
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentContract, setPaymentContract] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '', payment_type: 'deposit', payment_method: 'cash', reference_number: '', notes: ''
  });
  const [savingPayment, setSavingPayment] = useState(false);
  
  // Client portal
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [generatedAccessCode, setGeneratedAccessCode] = useState('');
  const [selectedContractForAccess, setSelectedContractForAccess] = useState(null);
  const [isSendingAccessEmail, setIsSendingAccessEmail] = useState(false);
  const [accessEmailSent, setAccessEmailSent] = useState(false);
  
  // Event brief
  const [showEventBriefModal, setShowEventBriefModal] = useState(false);
  const [selectedContractForBrief, setSelectedContractForBrief] = useState(null);

  // Effects
  useEffect(() => {
    const barTypePricing = { 'host_bar': 6.00, 'toonie_bar': 4.00, 'ticket_bar': 6.00, 'cash_bar': null };
    if (formData.barType && barTypePricing[formData.barType] !== undefined) {
      const drinkPrice = barTypePricing[formData.barType];
      const guestCount = parseInt(formData.guestCount) || 0;
      const drinksPerPerson = formData.drinksPerPerson || 3;
      const estimatedDrinkTotal = drinkPrice ? drinkPrice * drinksPerPerson * guestCount : null;
      setFormData(prev => ({ ...prev, drinkPrice, estimatedDrinkTotal }));
    }
  }, [formData.barType, formData.guestCount, formData.drinksPerPerson]);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { filterContracts(); }, [eventTypeFilter, contracts, showPastEvents, searchTerm, sortMode, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contractsData, menusData, clientsData, rentalsData, equipmentData, hdItems, lnItems, buffItems, addItems, platItems, barPkgs, ottItems, eventTypesData] = await Promise.all([
        contractsAPI.getAll(), menusAPI.getAll(), clientsAPI.getAll(),
        rentalsAPI.getAll(), equipmentAPI.getAll(),
        selectableItemsAPI.getHorsDoeuvres(), selectableItemsAPI.getLateNight(),
        selectableItemsAPI.getBuffet(), selectableItemsAPI.getAddons(),
        selectableItemsAPI.getPlated(), selectableItemsAPI.getBar(),
        selectableItemsAPI.getOnTheTurn().catch(() => []),
        eventTypesAPI.getAll().catch(() => [])
      ]);
      setContracts(contractsData); setMenus(menusData); setClients(clientsData);
      setRentals(rentalsData); setEquipment(equipmentData);
      setHorsDoeuvresItems(hdItems); setLateNightItems(lnItems); setBuffetItems(buffItems);
      setAddonItems(addItems); setPlatedItems(platItems); setBarItems(barPkgs);
      setOnTheTurnItems(ottItems);
      setEventTypes(eventTypesData);
      setPackageTemplates(await eventPackageTemplatesAPI.getAll().catch(() => []));
    } catch (error) {
      toast({ title: 'Error loading data', description: error.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const filterContracts = () => {
    let base = eventTypeFilter === 'all'
      ? [...contracts]
      : contracts.filter(c => c.event_type === eventTypeFilter);
    // Status filter (Pending / Confirmed / Completed)
    if (statusFilter && statusFilter !== 'all') {
      base = base.filter(c => (c.status || 'pending') === statusFilter);
    }
    // Hide completed OR past-date contracts unless "Show Past Events" is on
    if (!showPastEvents) {
      const today = new Date().toISOString().split('T')[0];
      base = base.filter(c => c.status !== 'completed' && (c.event_date >= today || !c.event_date));
    }
    // Free-text search across client name, event name, contract #, secondary client
    const q = (searchTerm || '').trim().toLowerCase();
    if (q) {
      base = base.filter(c =>
        (c.client_name || '').toLowerCase().includes(q)
        || (c.secondary_client_name || '').toLowerCase().includes(q)
        || (c.event_name || '').toLowerCase().includes(q)
        || (c.contract_number || '').toLowerCase().includes(q)
        || (c.location || '').toLowerCase().includes(q)
      );
    }
    // Sort
    base.sort((a, b) => {
      if (sortMode === 'client_asc') {
        return (a.client_name || '').localeCompare(b.client_name || '');
      }
      if (sortMode === 'status') {
        // Order: pending → confirmed → completed, then by event date within each group
        const rank = { pending: 0, confirmed: 1, completed: 2 };
        const ra = rank[a.status || 'pending'] ?? 3;
        const rb = rank[b.status || 'pending'] ?? 3;
        if (ra !== rb) return ra - rb;
        return (a.event_date || '').localeCompare(b.event_date || '');
      }
      // default: event date ascending (soonest first)
      const da = a.event_date || '';
      const db = b.event_date || '';
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db);
    });
    setFilteredContracts(base);
  };

  const sortMenuItems = (items) => {
    if (!items || items.length === 0) return [];
    const order = { 'salad':1,'salads':1,'soup':2,'soups':2,'appetizer':3,'appetizers':3,'starter':3,'starters':3,
      'mains':4,'main':4,'entree':4,'entrees':4,'protein':4,'sides':5,'side':5,'vegetable':5,'vegetables':5,
      'starch':5,'dessert':6,'desserts':6,'beverage':7,'beverages':7,'coffee':7,'drinks':7 };
    return [...items].sort((a, b) => (order[(a.category||'').toLowerCase()]||99) - (order[(b.category||'').toLowerCase()]||99));
  };

  const getPlatedCost = () => selectedPlatedMains.reduce((sum, m) => sum + (m.quantity * (m.item.price_per_person || 0)), 0);
  const getPlatedMainsTotal = () => selectedPlatedMains.reduce((sum, m) => sum + m.quantity, 0);

  const getRequiredHDItemCount = () => {
    const selectedHDMenus = menus.filter(m => selectedMenuIds.includes(m.id) && m.category === "Hors d'Oeuvres");
    for (const menu of selectedHDMenus) {
      if (menu.name.includes('6')) return { total: 6, hot: 3, cold: 3 };
      if (menu.name.includes('4')) return { total: 4, hot: 0, cold: 0 };
    }
    return { total: 0, hot: 0, cold: 0 };
  };

  const getAvailableMenus = () => menus.filter(m => {
    const et = formData.eventType;
    // Golf Tournament should also see Tournament menus
    if (et === 'golf_tournament') return m.event_type === 'golf_tournament' || m.event_type === 'tournament' || m.available_for?.includes(et);
    return m.event_type === et || m.available_for?.includes(et);
  });

  const getMenusByCategory = () => {
    const availableMenus = getAvailableMenus();
    const categoryOrder = ['Buffet', 'Premium Buffet', 'Plated', 'Interactive', "Hors d'Oeuvres", 'Late Night', 'Tournament', 'bar_package'];
    const categories = {};
    availableMenus.forEach(menu => { const cat = menu.category || 'Other'; if (!categories[cat]) categories[cat] = []; categories[cat].push(menu); });
    const sorted = {};
    categoryOrder.forEach(cat => { if (categories[cat]) sorted[cat] = categories[cat]; });
    Object.keys(categories).forEach(cat => { if (!sorted[cat]) sorted[cat] = categories[cat]; });
    return sorted;
  };

  const formatCategoryName = (category) => {
    const names = { 'Buffet':'Dinner Buffets','Premium Buffet':'Premium Buffets','Plated':'Plated Dinners',
      'Interactive':'Interactive Stations',"Hors d'Oeuvres":'Hors d\'Oeuvres (Cocktail Hour)',
      'Late Night':'Late Night Buffets','Tournament':'Tournament Menus (All-Inclusive)','bar_package':'Bar Packages' };
    return names[category] || category;
  };

  const calculateTotals = () => {
    const guestCount = parseInt(formData.guestCount) || 0;
    const overrides = formData.menuPriceOverrides || {};
    const effectivePrice = (m) => {
      const ov = overrides[m.id];
      return (ov !== undefined && ov !== null && ov !== '') ? parseFloat(ov) || 0 : (m.price_per_person || 0);
    };
    const selectedMenusData = menus.filter(m => selectedMenuIds.includes(m.id));
    const rentalsSubtotal = selectedRentals.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const allInclusiveMenus = selectedMenusData.filter(m => m.is_all_inclusive);
    const flatPackageMenus = selectedMenusData.filter(m => m.package_price && !m.price_per_person && !m.is_all_inclusive);
    const regularMenus = selectedMenusData.filter(m => !m.is_all_inclusive && m.category !== "Plated" && !(m.package_price && !m.price_per_person));
    const regularMenuSubtotal = regularMenus.reduce((sum, menu) => sum + effectivePrice(menu), 0) * guestCount;
    const flatPackageSubtotal = flatPackageMenus.reduce((sum, menu) => sum + (menu.package_price || 0), 0);
    const platedSubtotal = getPlatedCost();
    const addonsSubtotal = selectedAddons.reduce((sum, item) => sum + (item.price_per_person || 0), 0) * guestCount;
    const customAddonsSubtotal = (formData.customAddons || []).reduce((sum, addon) => sum + (addon.total || 0), 0);
    // Custom menu (per phase) — supports multi-tier pricing (qty × $/person) with
    // a fallback to legacy single per-person price × guest_count when tiers are empty.
    const phaseTotal = (tiers, fallbackPerPerson, guests) => {
      const validTiers = (tiers || []).filter(t => {
        const q = parseInt(t.quantity); const p = parseFloat(t.price_per_person);
        return q > 0 && p > 0;
      });
      if (validTiers.length > 0) {
        return validTiers.reduce((s, t) => s + (parseInt(t.quantity) || 0) * (parseFloat(t.price_per_person) || 0), 0);
      }
      const pp = parseFloat(fallbackPerPerson) || 0;
      return pp * (parseInt(guests) || 0);
    };
    const customMenuSubtotal = [
      phaseTotal(formData.customMenuBreakfastTiers, formData.customMenuBreakfastPrice, guestCount),
      phaseTotal(formData.customMenuLunchTiers, formData.customMenuLunchPrice, guestCount),
      phaseTotal(formData.customMenuCocktailTiers, formData.customMenuCocktailPrice, guestCount),
      phaseTotal(formData.customMenuDinnerTiers, formData.customMenuDinnerPrice, guestCount),
      phaseTotal(formData.customMenuDessertTiers, formData.customMenuDessertPrice, guestCount),
      phaseTotal(formData.customMenuLateNightTiers, formData.customMenuLateNightPrice, guestCount),
      phaseTotal(formData.customMenuBarTiers, formData.customMenuBarPrice, guestCount),
      phaseTotal(formData.customMenuBeverageTiers, formData.customMenuBeveragePrice, guestCount),
      phaseTotal(formData.customMenuOnTheTurnTiers, formData.customMenuOnTheTurnPrice, guestCount),
    ].reduce((s, v) => s + v, 0);
    // On the Turn items (tax included - per person)
    const onTheTurnSubtotal = selectedOnTheTurnItems.reduce((sum, item) => sum + (item.price_per_person || 0) * (item.quantity || 0), 0);
    let allInclusiveBase = 0, allInclusiveTax = 0, allInclusiveGratuity = 0;
    allInclusiveMenus.forEach(menu => {
      const allInPrice = effectivePrice(menu) * guestCount;
      const taxRate = menu.tax_rate || 0.13; const gratRate = menu.included_gratuity_rate || 0.15;
      const basePrice = allInPrice / (1 + taxRate + gratRate);
      allInclusiveBase += basePrice; allInclusiveTax += basePrice * taxRate; allInclusiveGratuity += basePrice * gratRate;
    });
    const menuSubtotal = regularMenuSubtotal + flatPackageSubtotal + allInclusiveBase + platedSubtotal + addonsSubtotal + customAddonsSubtotal + customMenuSubtotal;
    const currentTravelFee = formData.eventType === 'cocktail_coach' ? travelFee : 0;
    const drinkTotal = formData.estimatedDrinkTotal || 0;
    
    // Calculate discount
    const discountAmount = (formData.discounts || []).reduce((sum, d) => {
      if (d.type === 'percentage') return sum + (menuSubtotal * (d.value / 100));
      return sum + (d.value || 0);
    }, 0);
    
    const subtotal = menuSubtotal + rentalsSubtotal + currentTravelFee + drinkTotal + onTheTurnSubtotal - discountAmount;
    const isTournament = formData.eventType === 'tournament' || formData.eventType === 'golf_tournament';
    const taxRate = 0.13;
    const taxableBase = isTournament ? 0 : (regularMenuSubtotal + flatPackageSubtotal + rentalsSubtotal + platedSubtotal + addonsSubtotal + customAddonsSubtotal + customMenuSubtotal + currentTravelFee);
    // Gratuity is computed first so it can be included in the tax base (auto-gratuity / service charges are taxable in ON)
    const gratuityRate = (!isTournament && formData.includeGratuity) ? parseFloat(formData.gratuityRate) : 0;
    const regularGratuity = gratuityRate ? taxableBase * gratuityRate : 0;
    const gratuityAmount = regularGratuity + allInclusiveGratuity;
    // Tax applies to the taxable base + the regular gratuity (all-inclusive menus already have tax baked in)
    const regularTax = (taxableBase + regularGratuity) * taxRate;
    const taxAmount = regularTax + allInclusiveTax;
    const total = subtotal + taxAmount + gratuityAmount;
    return { menuSubtotal, rentalsSubtotal, platedSubtotal, addonsSubtotal, travelFee: currentTravelFee,
      drinkTotal, discountAmount, onTheTurnSubtotal, subtotal, taxAmount, gratuityAmount, total, hasAllInclusive: allInclusiveMenus.length > 0,
      hasPlated: selectedMenusData.filter(m => m.category === "Plated").length > 0 };
  };

  // Toggle functions
  const updateMenuPriceOverride = (menuId, value) => {
    setFormData(prev => {
      const next = { ...(prev.menuPriceOverrides || {}) };
      if (value === '' || value === null || value === undefined) {
        delete next[menuId];
      } else {
        next[menuId] = value;
      }
      return { ...prev, menuPriceOverrides: next };
    });
  };

  const toggleMenuSelection = (menuId) => {
    const menu = menus.find(m => m.id === menuId);
    const isSelected = selectedMenuIds.includes(menuId);
    setSelectedMenuIds(prev => isSelected ? prev.filter(id => id !== menuId) : [...prev, menuId]);
    if (isSelected) {
      // Clear override when deselecting
      setFormData(prev => {
        const next = { ...(prev.menuPriceOverrides || {}) };
        delete next[menuId];
        return { ...prev, menuPriceOverrides: next };
      });
    }
    if (menu?.category === "Hors d'Oeuvres") {
      if (isSelected) { const others = selectedMenuIds.filter(id => id !== menuId && menus.find(m => m.id === id)?.category === "Hors d'Oeuvres"); if (others.length === 0) { setShowHorsDoeuvresSection(false); setSelectedHorsDoeuvresItems([]); } } else setShowHorsDoeuvresSection(true);
    }
    if (menu?.category === "Late Night") {
      if (isSelected) { const others = selectedMenuIds.filter(id => id !== menuId && menus.find(m => m.id === id)?.category === "Late Night"); if (others.length === 0) { setShowLateNightSection(false); setSelectedLateNightItems([]); } } else setShowLateNightSection(true);
    }
    if (menu?.requires_item_selection && menu?.category !== "Hors d'Oeuvres" && menu?.category !== "Late Night" && menu?.category !== "Plated") {
      if (isSelected) { const others = selectedMenuIds.filter(id => { const m = menus.find(m => m.id === id); return id !== menuId && m?.requires_item_selection && m?.category !== "Hors d'Oeuvres" && m?.category !== "Late Night" && m?.category !== "Plated"; }); if (others.length === 0) { setShowBuffetSection(false); setSelectedBuffetItems([]); } } else setShowBuffetSection(true);
    }
    if (menu?.category === "Plated") {
      if (isSelected) { const others = selectedMenuIds.filter(id => id !== menuId && menus.find(m => m.id === id)?.category === "Plated"); if (others.length === 0) { setShowPlatedSection(false); setSelectedPlatedAppetizer(null); setSelectedPlatedSalad(null); setSelectedPlatedMains([]); setSelectedPlatedDessert(null); setPlatedCourses(3); } } else { setShowPlatedSection(true); setPlatedCourses(menu.courses || 3); }
    }
  };

  const toggleItem = (setter) => (item) => setter(prev => prev.find(i => i.id === item.id) ? prev.filter(i => i.id !== item.id) : [...prev, item]);
  const toggleHorsDoeuvresItem = toggleItem(setSelectedHorsDoeuvresItems);
  const toggleLateNightItem = toggleItem(setSelectedLateNightItems);
  const toggleBarItem = toggleItem(setSelectedBarItems);
  const toggleBuffetItem = toggleItem(setSelectedBuffetItems);
  const toggleAddon = toggleItem(setSelectedAddons);
  const toggleOnTheTurnItem = (item) => {
    setSelectedOnTheTurnItems(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) return prev.filter(i => i.id !== item.id);
      return [...prev, { ...item, quantity: parseInt(formData.guestCount) || 1 }];
    });
  };
  const updateOnTheTurnQuantity = (itemId, quantity) => {
    setSelectedOnTheTurnItems(prev => prev.map(i => i.id === itemId ? { ...i, quantity: parseInt(quantity) || 0 } : i));
  };

  const selectPlatedAppetizer = (item) => setSelectedPlatedAppetizer(selectedPlatedAppetizer?.id === item.id ? null : item);
  const selectPlatedSalad = (item) => setSelectedPlatedSalad(selectedPlatedSalad?.id === item.id ? null : item);
  const selectPlatedDessert = (item) => setSelectedPlatedDessert(selectedPlatedDessert?.id === item.id ? null : item);
  const togglePlatedMain = (item) => setSelectedPlatedMains(prev => { const e = prev.find(i => i.item.id === item.id); if (e) return prev.filter(i => i.item.id !== item.id); if (prev.length < 3) return [...prev, { item, quantity: 0 }]; return prev; });
  const updatePlatedMainQuantity = (itemId, quantity) => setSelectedPlatedMains(prev => prev.map(m => m.item.id === itemId ? { ...m, quantity: Math.max(0, quantity) } : m));

  const toggleRentalSelection = (rental) => setSelectedRentals(prev => prev.find(r => r.id === rental.id) ? prev.filter(r => r.id !== rental.id) : [...prev, { id: rental.id, name: rental.name, quantity: 1, price: rental.price, category: rental.category, color: rental.color_options?.[0] || null }]);
  const updateRentalQuantity = (id, qty) => setSelectedRentals(prev => prev.map(r => r.id === id ? { ...r, quantity: Math.max(1, qty) } : r));
  const updateRentalColor = (id, color) => setSelectedRentals(prev => prev.map(r => r.id === id ? { ...r, color } : r));
  const calculateRentalsTotal = () => selectedRentals.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const toggleEquipmentSelection = (item) => setSelectedEquipment(prev => prev.find(e => e.id === item.id) ? prev.filter(e => e.id !== item.id) : [...prev, { id: item.id, name: item.name, quantity: 1, category: item.category }]);
  const updateEquipmentQuantity = (id, qty) => setSelectedEquipment(prev => prev.map(e => e.id === id ? { ...e, quantity: Math.max(1, qty) } : e));

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedMenusData = menus.filter(m => selectedMenuIds.includes(m.id));
      const overrides = formData.menuPriceOverrides || {};
      const effectivePrice = (m) => {
        const ov = overrides[m.id];
        return (ov !== undefined && ov !== null && ov !== '') ? parseFloat(ov) || 0 : (m.price_per_person || 0);
      };
      const perPersonMenuTotal = selectedMenusData.filter(menu => menu.category !== "Plated" && !menu.price_from_selections && (!menu.package_price || menu.price_per_person)).reduce((sum, menu) => sum + effectivePrice(menu), 0) * parseInt(formData.guestCount);
      const flatPackageTotal = selectedMenusData.filter(menu => menu.package_price && !menu.price_per_person).reduce((sum, menu) => sum + menu.package_price, 0);
      const menuSubtotal = perPersonMenuTotal + flatPackageTotal;
      const platedTotal = getPlatedCost();
      const rentalsTotal = selectedRentals.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const addonsTotal = selectedAddons.reduce((sum, item) => sum + (item.price_per_person || 0), 0) * parseInt(formData.guestCount);
      const customAddonsTotal = (formData.customAddons || []).reduce((sum, addon) => sum + (addon.total || 0), 0);
      const phaseTotalSave = (tiers, fallbackPerPerson, guests) => {
        const validTiers = (tiers || []).filter(t => {
          const q = parseInt(t.quantity); const p = parseFloat(t.price_per_person);
          return q > 0 && p > 0;
        });
        if (validTiers.length > 0) {
          return validTiers.reduce((s, t) => s + (parseInt(t.quantity) || 0) * (parseFloat(t.price_per_person) || 0), 0);
        }
        const pp = parseFloat(fallbackPerPerson) || 0;
        return pp * (parseInt(guests) || 0);
      };
      const g2 = parseInt(formData.guestCount || 0);
      const customMenuTotal = [
        phaseTotalSave(formData.customMenuBreakfastTiers, formData.customMenuBreakfastPrice, g2),
        phaseTotalSave(formData.customMenuLunchTiers, formData.customMenuLunchPrice, g2),
        phaseTotalSave(formData.customMenuCocktailTiers, formData.customMenuCocktailPrice, g2),
        phaseTotalSave(formData.customMenuDinnerTiers, formData.customMenuDinnerPrice, g2),
        phaseTotalSave(formData.customMenuDessertTiers, formData.customMenuDessertPrice, g2),
        phaseTotalSave(formData.customMenuLateNightTiers, formData.customMenuLateNightPrice, g2),
        phaseTotalSave(formData.customMenuBarTiers, formData.customMenuBarPrice, g2),
        phaseTotalSave(formData.customMenuBeverageTiers, formData.customMenuBeveragePrice, g2),
        phaseTotalSave(formData.customMenuOnTheTurnTiers, formData.customMenuOnTheTurnPrice, g2),
      ].reduce((s, v) => s + v, 0);
      const currentTravelFee = formData.eventType === 'cocktail_coach' ? travelFee : 0;
      
      // Calculate discount
      const preDiscountSubtotal = menuSubtotal + platedTotal + rentalsTotal + addonsTotal + customAddonsTotal + customMenuTotal + currentTravelFee;
      const discountTotal = (formData.discounts || []).reduce((sum, d) => {
        if (d.type === 'percentage') return sum + ((menuSubtotal + platedTotal + addonsTotal + customAddonsTotal) * (d.value / 100));
        return sum + (d.value || 0);
      }, 0);
      const subtotal = preDiscountSubtotal - discountTotal;
      const isTournament = formData.eventType === 'tournament' || formData.eventType === 'golf_tournament';
      const taxRate = 0.13;
      // Tournament menus are tax & gratuity included — no additional charges
      const gratuityRate = (!isTournament && formData.includeGratuity) ? parseFloat(formData.gratuityRate) : null;
      const gratuityAmount = gratuityRate ? subtotal * gratuityRate : 0;
      const taxAmount = isTournament ? 0 : (subtotal + gratuityAmount) * taxRate;
      const totalAmount = subtotal + taxAmount + gratuityAmount;

      const selectedMenusWithItems = selectedMenusData.map(menu => {
        const isHD = menu.category === "Hors d'Oeuvres"; const isLN = menu.category === "Late Night";
        const isPlated = menu.category === "Plated";
        const isBuf = menu.requires_item_selection && !isHD && !isLN && !isPlated;
        let menuItems = [];
        if (isHD && selectedHorsDoeuvresItems.length > 0) menuItems = selectedHorsDoeuvresItems.map(item => ({ id: item.id, name: item.name, category: item.sub_category, has_upcharge: item.has_upcharge }));
        else if (isLN && selectedLateNightItems.length > 0) menuItems = selectedLateNightItems.map(item => ({ id: item.id, name: item.name, category: 'late_night', description: item.description }));
        else if (isBuf && selectedBuffetItems.length > 0) menuItems = selectedBuffetItems.map(item => ({ id: item.id, name: item.name, category: item.sub_category, description: item.description, has_upcharge: item.has_upcharge, upcharge_amount: item.upcharge_amount }));
        else if (isPlated) {
          if (selectedPlatedAppetizer) menuItems.push({ id: selectedPlatedAppetizer.id, name: selectedPlatedAppetizer.name, category: 'appetizer', description: selectedPlatedAppetizer.description });
          if (selectedPlatedSalad) menuItems.push({ id: selectedPlatedSalad.id, name: selectedPlatedSalad.name, category: 'salad', description: selectedPlatedSalad.description });
          selectedPlatedMains.forEach(m => menuItems.push({ id: m.item.id, name: m.item.name, category: 'main', description: m.item.description, quantity: m.quantity, has_upcharge: m.item.has_upcharge, upcharge_amount: m.item.upcharge_amount, price_per_person: m.item.price_per_person }));
          if (selectedPlatedDessert) menuItems.push({ id: selectedPlatedDessert.id, name: selectedPlatedDessert.name, category: 'dessert', description: selectedPlatedDessert.description });
        } else menuItems = sortMenuItems(menu.items || []);
        return { id: menu.id, name: menu.name, category: menu.category, price_per_person: effectivePrice(menu), price_overridden: overrides[menu.id] !== undefined && overrides[menu.id] !== '' && overrides[menu.id] !== null, items: menuItems };
      });

      const contractData = {
        client_name: formData.clientName, client_email: formData.clientEmail, client_phone: formData.clientPhone,
        client_id: formData.clientId || null, billing_address: formData.billingAddress || null,
        secondary_client_name: formData.secondaryClientName || null, secondary_client_email: formData.secondaryClientEmail || null,
        secondary_client_phone: formData.secondaryClientPhone || null, event_name: formData.eventName,
        event_type: formData.eventType, event_date: formData.eventDate, event_time: formData.eventTime,
        event_start_time: formData.eventStartTime || null,
        breakfast_time: formData.breakfastTime || null, brunch_time: formData.brunchTime || null,
        lunch_time: formData.lunchTime || null,
        cocktail_hour_time: formData.cocktailHourTime || null,
        dinner_start_time: formData.dinnerStartTime || null, late_night_time: formData.lateNightTime || null,
        event_end_time: formData.eventEndTime || null, guest_count: parseInt(formData.guestCount),
        location: formData.location, location_type: formData.locationType,
        hors_doeuvres_service: formData.horsDoeuvresService || null,
        chafing_dish_count: formData.chafingDishCount === '' ? null : (parseInt(formData.chafingDishCount) || null),
        service_style: formData.serviceStyle || null,
        room: formData.room || null,
        bar_type: formData.barType || null, drinks_per_person: formData.drinksPerPerson || 3,
        drink_price: formData.drinkPrice, estimated_drink_total: formData.estimatedDrinkTotal,
        selected_menus: selectedMenuIds, selected_menus_details: selectedMenusWithItems,
        menu_price_overrides: Object.fromEntries(
          Object.entries(overrides).filter(([k, v]) => v !== '' && v !== null && v !== undefined && !isNaN(parseFloat(v))).map(([k, v]) => [k, parseFloat(v)])
        ),
        selected_rentals: selectedRentals, selected_equipment: selectedEquipment,
        selected_hors_doeuvres: selectedHorsDoeuvresItems, selected_late_night: selectedLateNightItems,
        selected_buffet: selectedBuffetItems, selected_bar: selectedBarItems,
        selected_addons: selectedAddons, addons_subtotal: addonsTotal,
        selected_on_the_turn: selectedOnTheTurnItems,
        on_the_turn_subtotal: selectedOnTheTurnItems.reduce((sum, i) => sum + (i.price_per_person || 0) * (i.quantity || 0), 0),
        custom_addons: formData.customAddons || [], custom_addons_subtotal: customAddonsTotal,
        discounts: formData.discounts || [], discount_total: discountTotal,
        plated_subtotal: platedTotal, selected_plated_appetizer: selectedPlatedAppetizer,
        selected_plated_salad: selectedPlatedSalad, selected_plated_mains: selectedPlatedMains,
        selected_plated_dessert: selectedPlatedDessert, plated_courses: platedCourses,
        table_style: formData.tableStyle || null, tablecloth_color: formData.tableclothColor || null,
        napkin_color: formData.napkinColor || null, dj_table: formData.djTable, gift_table: formData.giftTable,
        head_table: formData.headTable, water_station: formData.waterStation, coffee_station: formData.coffeeStation,
        bar_setup: formData.barSetup, cake_table: formData.cakeTable, registration_table: formData.registrationTable,
        dessert_station: formData.dessertStation,
        tournament_setup: formData.tournamentSetup, prize_tables: formData.prizeTables,
        see_floorplan: formData.seeFloorplan, salad_station: formData.saladStation,
        buffet_light_up_wall: formData.buffetLightUpWall, buffet_front_of_room: formData.buffetFrontOfRoom,
        dessert_bay_window: formData.dessertBayWindow, cocktail_tables: formData.cocktailTables,
        podium: formData.podium, microphone: formData.microphone,
        special_requests: formData.specialRequests,
        kitchen_notes: formData.kitchenNotes || null,
        assigned_staff: formData.assignedStaff || [],
        // Legacy `custom_menu` is only used for pickup/dropoff events.
        // For all other event types, force empty string so per-phase fields are the single source of truth.
        // (Empty string '' is persisted via update_data filter; null would be dropped on PUT.)
        custom_menu: (formData.eventType === 'pickup' || formData.eventType === 'dropoff') ? (formData.customMenu || null) : '',
        custom_menu_breakfast: formData.customMenuBreakfast || null,
        custom_menu_lunch: formData.customMenuLunch || null,
        custom_menu_cocktail: formData.customMenuCocktail || null,
        custom_menu_dinner: formData.customMenuDinner || null,
        custom_menu_dessert: formData.customMenuDessert || null,
        coffee_service: !!formData.coffeeService,
        coffee_notes: formData.coffeeNotes || null,
        custom_menu_late_night: formData.customMenuLateNight || null,
        custom_menu_bar: formData.customMenuBar || null,
        custom_menu_beverage: formData.customMenuBeverage || null,
        custom_menu_on_the_turn: formData.customMenuOnTheTurn || null,
        custom_menu_breakfast_price: parseFloat(formData.customMenuBreakfastPrice) || null,
        custom_menu_lunch_price: parseFloat(formData.customMenuLunchPrice) || null,
        custom_menu_cocktail_price: parseFloat(formData.customMenuCocktailPrice) || null,
        custom_menu_dinner_price: parseFloat(formData.customMenuDinnerPrice) || null,
        custom_menu_dessert_price: parseFloat(formData.customMenuDessertPrice) || null,
        custom_menu_late_night_price: parseFloat(formData.customMenuLateNightPrice) || null,
        custom_menu_bar_price: parseFloat(formData.customMenuBarPrice) || null,
        custom_menu_beverage_price: parseFloat(formData.customMenuBeveragePrice) || null,
        custom_menu_on_the_turn_price: parseFloat(formData.customMenuOnTheTurnPrice) || null,
        // Tier-based pricing (sanitized: only persist tiers that have any data)
        custom_menu_breakfast_tiers: sanitizeTiers(formData.customMenuBreakfastTiers),
        custom_menu_lunch_tiers: sanitizeTiers(formData.customMenuLunchTiers),
        custom_menu_cocktail_tiers: sanitizeTiers(formData.customMenuCocktailTiers),
        custom_menu_dinner_tiers: sanitizeTiers(formData.customMenuDinnerTiers),
        custom_menu_dessert_tiers: sanitizeTiers(formData.customMenuDessertTiers),
        custom_menu_late_night_tiers: sanitizeTiers(formData.customMenuLateNightTiers),
        custom_menu_bar_tiers: sanitizeTiers(formData.customMenuBarTiers),
        custom_menu_beverage_tiers: sanitizeTiers(formData.customMenuBeverageTiers),
        custom_menu_on_the_turn_tiers: sanitizeTiers(formData.customMenuOnTheTurnTiers),
        dietary_vegan: parseInt(formData.dietaryVegan) || 0,
        dietary_vegetarian: parseInt(formData.dietaryVegetarian) || 0,
        dietary_gluten_free: parseInt(formData.dietaryGlutenFree) || 0,
        dietary_nut_allergy: parseInt(formData.dietaryNutAllergy) || 0,
        dietary_dairy_free: parseInt(formData.dietaryDairyFree) || 0,
        dietary_halal: parseInt(formData.dietaryHalal) || 0,
        dietary_kosher: parseInt(formData.dietaryKosher) || 0,
        dietary_other: formData.dietaryOther || null,
        delivery_contact_name: formData.deliveryContactName || null,
        delivery_contact_phone: formData.deliveryContactPhone || null,
        travel_fee: currentTravelFee, travel_distance_km: formData.eventType === 'cocktail_coach' ? travelDistanceKm : null,
        travel_chargeable_km: formData.eventType === 'cocktail_coach' ? travelChargeableKm : null,
        subtotal, tax_amount: taxAmount, gratuity_rate: gratuityRate, gratuity_amount: gratuityAmount,
        total_amount: totalAmount, tax_rate: taxRate
      };

      if (editingContract) { await contractsAPI.update(editingContract.id, contractData); toast({ title: 'Contract updated!' }); }
      else { await contractsAPI.create(contractData); toast({ title: 'Contract created!' }); }
      setIsDialogOpen(false); resetForm(); fetchData();
    } catch (error) { toast({ title: `Error ${editingContract ? 'updating' : 'creating'} contract`, description: error.message, variant: 'destructive' }); }
  };

  const openEditContract = (contract) => {
    setEditingContract(contract);
    setFormData({
      clientName: contract.client_name || '', clientEmail: contract.client_email || '', clientPhone: contract.client_phone || '',
      clientId: contract.client_id || null, secondaryClientName: contract.secondary_client_name || '',
      secondaryClientEmail: contract.secondary_client_email || '', secondaryClientPhone: contract.secondary_client_phone || '',
      billingAddress: contract.billing_address || '', eventName: contract.event_name || '',
      eventType: contract.event_type || 'offsite', eventDate: contract.event_date || '',
      eventTime: contract.event_time || '', guestCount: contract.guest_count?.toString() || '',
      location: contract.location || '', locationType: contract.location_type || 'onsite',
      horsDoeuvresService: contract.hors_doeuvres_service || '',
      chafingDishCount: contract.chafing_dish_count ?? '',
      specialRequests: contract.special_requests || '', includeGratuity: contract.gratuity_rate > 0,
      kitchenNotes: contract.kitchen_notes || '',
      assignedStaff: contract.assigned_staff || [],
      gratuityRate: contract.gratuity_rate?.toString() || '0.18', serviceStyle: contract.service_style || '',
      room: contract.room || '',
      barType: contract.bar_type || '', eventStartTime: contract.event_start_time || '',
      breakfastTime: contract.breakfast_time || '', brunchTime: contract.brunch_time || '',
      lunchTime: contract.lunch_time || '',
      cocktailHourTime: contract.cocktail_hour_time || '', dinnerStartTime: contract.dinner_start_time || '',
      lateNightTime: contract.late_night_time || '', eventEndTime: contract.event_end_time || '',
      tableStyle: contract.table_style || '', tableclothColor: contract.tablecloth_color || '',
      napkinColor: contract.napkin_color || '', djTable: contract.dj_table || false,
      giftTable: contract.gift_table || false, headTable: contract.head_table || false,
      waterStation: contract.water_station || false, coffeeStation: contract.coffee_station || false,
      barSetup: contract.bar_setup || false, cakeTable: contract.cake_table || false,
      registrationTable: contract.registration_table || false, dessertStation: contract.dessert_station || false,
      tournamentSetup: contract.tournament_setup || false, prizeTables: contract.prize_tables || false,
      seeFloorplan: contract.see_floorplan || false, saladStation: contract.salad_station || false,
      buffetLightUpWall: contract.buffet_light_up_wall || false, buffetFrontOfRoom: contract.buffet_front_of_room || false,
      dessertBayWindow: contract.dessert_bay_window || false, cocktailTables: contract.cocktail_tables || false,
      podium: contract.podium || false, microphone: contract.microphone || false,
      drinksPerPerson: contract.drinks_per_person || 3, drinkPrice: contract.drink_price || null,
      estimatedDrinkTotal: contract.estimated_drink_total || null, customAddons: contract.custom_addons || [],
      menuPriceOverrides: contract.menu_price_overrides || {},
      discounts: contract.discounts || [],
      customMenu: contract.custom_menu || '',
      customMenuBreakfast: contract.custom_menu_breakfast || '',
      customMenuLunch: contract.custom_menu_lunch || '',
      customMenuCocktail: contract.custom_menu_cocktail || '',
      customMenuDinner: contract.custom_menu_dinner || '',
      customMenuDessert: contract.custom_menu_dessert || '',
      coffeeService: contract.coffee_service === true,
      coffeeNotes: contract.coffee_notes || '',
      customMenuLateNight: contract.custom_menu_late_night || '',
      customMenuBar: contract.custom_menu_bar || '',
      customMenuBeverage: contract.custom_menu_beverage || '',
      customMenuOnTheTurn: contract.custom_menu_on_the_turn || '',
      customMenuBreakfastPrice: contract.custom_menu_breakfast_price ?? '',
      customMenuLunchPrice: contract.custom_menu_lunch_price ?? '',
      customMenuCocktailPrice: contract.custom_menu_cocktail_price ?? '',
      customMenuDinnerPrice: contract.custom_menu_dinner_price ?? '',
      customMenuDessertPrice: contract.custom_menu_dessert_price ?? '',
      customMenuLateNightPrice: contract.custom_menu_late_night_price ?? '',
      customMenuBarPrice: contract.custom_menu_bar_price ?? '',
      customMenuBeveragePrice: contract.custom_menu_beverage_price ?? '',
      customMenuOnTheTurnPrice: contract.custom_menu_on_the_turn_price ?? '',
      customMenuBreakfastTiers: contract.custom_menu_breakfast_tiers || [],
      customMenuLunchTiers: contract.custom_menu_lunch_tiers || [],
      customMenuCocktailTiers: contract.custom_menu_cocktail_tiers || [],
      customMenuDinnerTiers: contract.custom_menu_dinner_tiers || [],
      customMenuDessertTiers: contract.custom_menu_dessert_tiers || [],
      customMenuLateNightTiers: contract.custom_menu_late_night_tiers || [],
      customMenuBarTiers: contract.custom_menu_bar_tiers || [],
      customMenuBeverageTiers: contract.custom_menu_beverage_tiers || [],
      customMenuOnTheTurnTiers: contract.custom_menu_on_the_turn_tiers || [],
      dietaryVegan: contract.dietary_vegan || 0,
      dietaryVegetarian: contract.dietary_vegetarian || 0,
      dietaryGlutenFree: contract.dietary_gluten_free || 0,
      dietaryNutAllergy: contract.dietary_nut_allergy || 0,
      dietaryDairyFree: contract.dietary_dairy_free || 0,
      dietaryHalal: contract.dietary_halal || 0,
      dietaryKosher: contract.dietary_kosher || 0,
      dietaryOther: contract.dietary_other || '',
      deliveryContactName: contract.delivery_contact_name || '',
      deliveryContactPhone: contract.delivery_contact_phone || ''
    });
    setSelectedMenuIds(contract.selected_menus || []); setSelectedRentals(contract.selected_rentals || []);
    setSelectedEquipment(contract.selected_equipment || []); setSelectedHorsDoeuvresItems(contract.selected_hors_doeuvres || []);
    setSelectedLateNightItems(contract.selected_late_night || []); setSelectedBuffetItems(contract.selected_buffet || []);
    setSelectedBarItems(contract.selected_bar || []); setSelectedAddons(contract.selected_addons || []);
    setSelectedOnTheTurnItems(contract.selected_on_the_turn || []);
    setShowOnTheTurnSection((contract.selected_on_the_turn || []).length > 0);
    setSelectedPlatedAppetizer(contract.selected_plated_appetizer || null); setSelectedPlatedSalad(contract.selected_plated_salad || null);
    setSelectedPlatedMains(contract.selected_plated_mains || []); setSelectedPlatedDessert(contract.selected_plated_dessert || null);
    setPlatedCourses(contract.plated_courses || 3);
    setShowTimingSection(!!contract.event_start_time || !!contract.cocktail_hour_time);
    setShowSetupSection(!!contract.table_style || !!contract.tablecloth_color);
    setShowRentalsSection((contract.selected_rentals || []).length > 0);
    setShowEquipmentSection((contract.selected_equipment || []).length > 0);
    setShowBarSection(!!contract.bar_type); setShowBarItemsSection((contract.selected_bar || []).length > 0);
    setShowTravelFeeSection(contract.event_type === 'cocktail_coach');
    setTravelFee(contract.travel_fee || 0); setTravelDistanceKm(contract.travel_distance_km || null); setTravelChargeableKm(contract.travel_chargeable_km || null);
    const hasHD = (contract.selected_menus || []).some(id => menus.find(m => m.id === id && m.category === "Hors d'Oeuvres"));
    setShowHorsDoeuvresSection(hasHD || (contract.selected_hors_doeuvres || []).length > 0);
    const hasLN = (contract.selected_menus || []).some(id => menus.find(m => m.id === id && m.category === "Late Night"));
    setShowLateNightSection(hasLN || (contract.selected_late_night || []).length > 0);
    const hasBuf = (contract.selected_menus || []).some(id => { const m = menus.find(m => m.id === id); return m?.requires_item_selection && m?.category !== "Hors d'Oeuvres" && m?.category !== "Late Night"; });
    setShowBuffetSection(hasBuf || (contract.selected_buffet || []).length > 0);
    setShowAddonsSection((contract.selected_addons || []).length > 0);
    setShowCustomAddonsSection((contract.custom_addons || []).length > 0);
    const hasPlated = (contract.selected_menus || []).some(id => menus.find(m => m.id === id && m.category === "Plated"));
    setShowPlatedSection(hasPlated || contract.selected_plated_salad || (contract.selected_plated_mains || []).length > 0);
    setShowCustomMenuSection(!!contract.custom_menu || !!contract.custom_menu_breakfast || !!contract.custom_menu_lunch || !!contract.custom_menu_cocktail || !!contract.custom_menu_dinner || !!contract.custom_menu_dessert || !!contract.custom_menu_late_night || !!contract.custom_menu_bar || !!contract.custom_menu_beverage || !!contract.custom_menu_on_the_turn || (contract.custom_menu_breakfast_tiers || []).length > 0 || (contract.custom_menu_lunch_tiers || []).length > 0 || (contract.custom_menu_cocktail_tiers || []).length > 0 || (contract.custom_menu_dinner_tiers || []).length > 0 || (contract.custom_menu_dessert_tiers || []).length > 0 || (contract.custom_menu_late_night_tiers || []).length > 0 || (contract.custom_menu_bar_tiers || []).length > 0 || (contract.custom_menu_beverage_tiers || []).length > 0 || (contract.custom_menu_on_the_turn_tiers || []).length > 0 || contract.event_type === 'pickup' || contract.event_type === 'dropoff');
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingContract(null); setFormData(INITIAL_FORM_DATA);
    setSelectedMenuIds([]); setSelectedRentals([]); setSelectedEquipment([]);
    setSelectedHorsDoeuvresItems([]); setSelectedLateNightItems([]); setSelectedBuffetItems([]);
    setSelectedBarItems([]); setSelectedAddons([]);
    setSelectedPlatedAppetizer(null); setSelectedPlatedSalad(null); setSelectedPlatedMains([]); setSelectedPlatedDessert(null);
    setPlatedCourses(3); setShowTimingSection(false); setShowSetupSection(false); setShowTablePlannerSection(false);
    setShowRentalsSection(false); setShowEquipmentSection(false); setShowBarSection(false);
    setShowTravelFeeSection(false); setTravelFee(0); setTravelDistanceKm(null); setTravelChargeableKm(null);
    setShowHorsDoeuvresSection(false); setShowLateNightSection(false); setShowBuffetSection(false);
    setShowBarItemsSection(false); setShowAddonsSection(false); setShowCustomAddonsSection(false); setShowPlatedSection(false);
    setShowCustomMenuSection(false); setShowOnTheTurnSection(false);
    setSelectedOnTheTurnItems([]);
  };

  const openRecordPayment = (contract) => {
    setPaymentContract(contract);
    const dep = contract.deposit_amount || (contract.total_amount * 0.25);
    const bal = contract.total_amount - (contract.total_paid || 0);
    setPaymentForm({ amount: contract.payment_status === 'deposit_paid' ? bal.toFixed(2) : dep.toFixed(2),
      payment_type: contract.payment_status === 'deposit_paid' ? 'balance' : 'deposit', payment_method: 'cash', reference_number: '', notes: '' });
    setShowPaymentModal(true);
  };

  const handleSavePayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) { toast({ title: 'Please enter a valid payment amount', variant: 'destructive' }); return; }
    setSavingPayment(true);
    try {
      await axios.post(`${API}/api/payments/record`, { contract_id: paymentContract.id, amount: parseFloat(paymentForm.amount),
        payment_type: paymentForm.payment_type, payment_method: paymentForm.payment_method,
        reference_number: paymentForm.reference_number || null, notes: paymentForm.notes || null });
      toast({ title: 'Payment Recorded!', description: `$${parseFloat(paymentForm.amount).toLocaleString()} payment recorded.` });
      setShowPaymentModal(false); setPaymentContract(null); fetchData();
    } catch (error) { toast({ title: 'Error recording payment', description: error.response?.data?.detail || 'Error.', variant: 'destructive' }); }
    finally { setSavingPayment(false); }
  };

  const handleDeleteContract = async (contractId) => {
    if (!window.confirm('Delete this contract? This cannot be undone.')) return;
    try { await contractsAPI.delete(contractId); toast({ title: 'Contract deleted' }); fetchData(); }
    catch (error) { toast({ title: 'Error deleting contract', description: error.message, variant: 'destructive' }); }
  };

  const handleStatusChange = async (contractId, newStatus) => {
    try {
      await contractsAPI.update(contractId, { status: newStatus });
      toast({ title: `Status updated to ${newStatus}` });
      fetchData();
    } catch (error) {
      toast({ title: 'Error updating status', variant: 'destructive' });
    }
  };

  const handleDuplicateContract = (contract) => {
    openEditContract({ ...contract, id: undefined, contract_number: undefined, status: 'pending', event_name: `${contract.event_name} (Copy)`, event_date: '', payments: [] });
    setEditingContract(null); // Treat as new, not edit
  };

  const applyTemplate = (templateId) => {
    const tpl = packageTemplates.find(t => t.id === templateId);
    if (!tpl) return;
    const timing = tpl.default_timing || {};
    setFormData(prev => ({
      ...prev,
      eventType: tpl.event_type || prev.eventType,
      serviceStyle: tpl.service_style || prev.serviceStyle,
      room: tpl.room || prev.room,
      locationType: tpl.location_type || prev.locationType,
      guestCount: tpl.default_guest_count ? String(tpl.default_guest_count) : prev.guestCount,
      eventStartTime: timing.eventStartTime || prev.eventStartTime,
      breakfastTime: timing.breakfastTime || prev.breakfastTime,
      brunchTime: timing.brunchTime || prev.brunchTime,
      lunchTime: timing.lunchTime || prev.lunchTime,
      cocktailHourTime: timing.cocktailHourTime || prev.cocktailHourTime,
      dinnerStartTime: timing.dinnerStartTime || prev.dinnerStartTime,
      lateNightTime: timing.lateNightTime || prev.lateNightTime,
      eventEndTime: timing.eventEndTime || prev.eventEndTime,
      assignedStaff: tpl.default_staff?.length ? tpl.default_staff : prev.assignedStaff,
      specialRequests: tpl.notes || prev.specialRequests,
    }));
    toast({ title: `Template "${tpl.name}" applied`, description: 'Timing, staff, and defaults pre-filled.' });
  };


  const generateClientAccess = async (contract) => {
    if (!contract.client_email) { toast({ title: 'No Client Email', variant: 'destructive' }); return; }
    try {
      const response = await clientPortalAPI.generateAccess({ client_email: contract.client_email, contract_id: contract.id });
      setGeneratedAccessCode(response.access_code); setSelectedContractForAccess(contract);
      setShowAccessCodeModal(true); setAccessEmailSent(false);
    } catch (error) { toast({ title: 'Error generating access code', variant: 'destructive' }); }
  };

  const sendAccessEmail = async (contract, accessCode) => {
    setIsSendingAccessEmail(true);
    try {
      await emailAPI.sendClientAccess({ to_email: contract.client_email, client_name: contract.client_name,
        event_name: contract.event_name, access_code: accessCode, portal_url: window.location.origin });
      setAccessEmailSent(true); toast({ title: 'Email sent!' });
    } catch (error) { toast({ title: 'Error sending email', variant: 'destructive' }); }
    finally { setIsSendingAccessEmail(false); }
  };

  return {
    contracts, filteredContracts, menus, clients, rentals, equipment, eventTypes, loading, eventTypeFilter, setEventTypeFilter,
    showPastEvents, setShowPastEvents,
    searchTerm, setSearchTerm, sortMode, setSortMode,
    statusFilter, setStatusFilter,
    isDialogOpen, setIsDialogOpen, editingContract, formData, setFormData,
    selectedMenuIds, setSelectedMenuIds, selectedRentals, setSelectedRentals,
    selectedEquipment, selectedHorsDoeuvresItems, selectedLateNightItems,
    selectedBuffetItems, selectedBarItems, selectedAddons,
    selectedPlatedAppetizer, selectedPlatedSalad, selectedPlatedMains, selectedPlatedDessert, platedCourses, setPlatedCourses,
    horsDoeuvresItems, lateNightItems, buffetItems, barItems, addonItems, platedItems,
    showTimingSection, setShowTimingSection, showSetupSection, setShowSetupSection,
    showTablePlannerSection, setShowTablePlannerSection, showRentalsSection, setShowRentalsSection,
    showEquipmentSection, setShowEquipmentSection, showBarSection, setShowBarSection,
    showTravelFeeSection, setShowTravelFeeSection, showHorsDoeuvresSection, showLateNightSection,
    showBuffetSection, showBarItemsSection, showAddonsSection, setShowAddonsSection,
    showCustomAddonsSection, setShowCustomAddonsSection, showPlatedSection,
    showCustomMenuSection, setShowCustomMenuSection,
    showOnTheTurnSection, setShowOnTheTurnSection,
    onTheTurnItems, selectedOnTheTurnItems, toggleOnTheTurnItem, updateOnTheTurnQuantity,
    travelFee, setTravelFee, travelDistanceKm, setTravelDistanceKm, travelChargeableKm, setTravelChargeableKm,
    showPaymentModal, setShowPaymentModal, paymentContract, setPaymentContract, paymentForm, setPaymentForm, savingPayment,
    showAccessCodeModal, setShowAccessCodeModal, generatedAccessCode, selectedContractForAccess,
    isSendingAccessEmail, accessEmailSent, setAccessEmailSent,
    showEventBriefModal, setShowEventBriefModal, selectedContractForBrief, setSelectedContractForBrief,
    handleSubmit, openEditContract, resetForm, openRecordPayment, handleSavePayment, handleDeleteContract, handleStatusChange, handleDuplicateContract,
    packageTemplates, applyTemplate,
    generateClientAccess, sendAccessEmail,
    toggleMenuSelection, toggleHorsDoeuvresItem, toggleLateNightItem, toggleBarItem, toggleBuffetItem, toggleAddon,
    updateMenuPriceOverride,
    selectPlatedAppetizer, selectPlatedSalad, selectPlatedDessert, togglePlatedMain, updatePlatedMainQuantity,
    toggleRentalSelection, updateRentalQuantity, updateRentalColor, toggleEquipmentSelection, updateEquipmentQuantity,
    calculateTotals, calculateRentalsTotal, getPlatedCost, getPlatedMainsTotal, getRequiredHDItemCount,
    getMenusByCategory, formatCategoryName, sortMenuItems, toast
  };
};
